import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import api, { API } from "@/lib/api";
import { computeBriefingNav, fuelPlans, computeWB, taxiFuelL } from "@/lib/navcalc";
import { fmt, minToHm, fmtSpeed } from "@/lib/units";
import { CHECKLIST_POINTS } from "@/components/briefing/checklistData";
import CommentsPanel from "@/components/briefing/CommentsPanel";
import { toast } from "sonner";
import {
  Navigation, Plane, CheckCircle2, Circle, FileText, Image as ImageIcon,
  Loader2, Compass, Fuel, Scale, ExternalLink,
} from "lucide-react";

export default function SharedBriefing() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const res = await axios.get(`${API}/shared/${token}`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Lien invalide");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const briefing = data?.briefing;
  const aircraft = data?.aircraft;
  const nav = useMemo(() => computeBriefingNav(briefing || {}, aircraft || {}), [briefing, aircraft]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#070C18] px-4 text-center">
        <div>
          <Compass className="mx-auto mb-3 h-10 w-10 text-slate-600" />
          <p className="text-slate-300">{error}</p>
          <Link to="/" className="mt-3 inline-block text-cyan-400 hover:text-cyan-300">Retour</Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#070C18]">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  const plans = fuelPlans(briefing, nav, aircraft);
  const wb = computeWB(aircraft || {}, briefing.weights || {}, Number(briefing.fuelStartL) || 0, taxiFuelL(briefing.fuel, aircraft?.fuelBurn), nav.totalFuel);
  const sections = nav.ret ? [["Aller", nav.out], ["Retour", nav.ret]] : [["", nav.out]];
  const doneCount = CHECKLIST_POINTS.filter((p) => briefing.checklist?.[p.id]?.done).length;

  const postComment = async (text, name) => {
    const loggedIn = !!localStorage.getItem("vfr_token");
    try {
      if (loggedIn) {
        await api.post(`/briefings/${briefing.id}/comments`, { text });
      } else {
        await axios.post(`${API}/shared/${token}/comments`, { text, author_name: name });
      }
    } catch {
      // fallback to guest endpoint if authed access is denied
      await axios.post(`${API}/shared/${token}/comments`, { text, author_name: name });
    }
    await load();
    toast.success("Commentaire ajouté");
  };

  return (
    <div className="grain min-h-screen bg-[#070C18]">
      <header className="sticky top-0 z-40 border-b border-slate-800/80 bg-[#070C18]/85 px-4 py-3 backdrop-blur-md sm:px-8">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-cyan-500/30 bg-cyan-500/10">
              <Navigation className="h-4 w-4 text-cyan-400" />
            </div>
            <div className="font-display text-sm font-bold tracking-wide text-slate-50">VFR NAV — Briefing partagé</div>
          </div>
          <span className="rounded-full border border-slate-700 bg-slate-900/60 px-3 py-1 text-[11px] uppercase tracking-wider text-slate-400">
            Lecture seule
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] space-y-6 px-4 py-8 sm:px-8">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-50 sm:text-3xl">{briefing.name}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-3 font-mono-data text-sm text-cyan-300">
            <span>{briefing.departure || "?"} → {briefing.arrival || "?"}</span>
            <span className="text-slate-600">·</span>
            <span className="text-slate-400">{aircraft?.registration || "—"} {aircraft?.model || ""}</span>
            <span className="flex items-center gap-1 text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> {doneCount}/11</span>
          </div>
          <p className="mt-1 text-xs text-slate-500">Préparé par {data.owner_email}</p>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Tile icon={Navigation} label="Distance" value={`${fmt(nav.totalDistance)} NM`} />
          <Tile icon={Compass} label="Temps de vol" value={minToHm(nav.totalTime)} />
          <Tile icon={Fuel} label="Carburant voyage" value={`${fmt(nav.totalFuel, 1)} L`} />
          <Tile icon={Plane} label="TAS" value={fmtSpeed(aircraft?.tasCruise)} small />
        </div>

        {/* Nav log */}
        {sections.map(([title, sec]) => (
        <div key={title} className="glass overflow-hidden rounded-2xl">
          <div className="border-b border-slate-800 px-5 py-3 font-display text-sm font-semibold uppercase tracking-wide text-cyan-400">Log de navigation{title ? ` — ${title}` : ""}</div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-left font-mono-data text-xs">
              <thead>
                <tr className="border-b border-slate-700 bg-slate-950/60 text-[10px] uppercase tracking-wider text-slate-400">
                  <th className="px-3 py-2">Point</th><th className="px-2 py-2">Alt</th><th className="px-2 py-2">Cap mag</th>
                  <th className="px-2 py-2">Cap comp</th><th className="px-2 py-2">Dist</th><th className="px-2 py-2">Vsol</th>
                  <th className="px-2 py-2">Temps</th><th className="px-2 py-2">Cumul</th><th className="px-2 py-2">Carbu</th>
                </tr>
              </thead>
              <tbody>
                {sec.rows.map((r, i) => (
                  <tr key={i} className={i % 2 ? "bg-slate-900/40" : "bg-slate-900/70"}>
                    <td className="px-3 py-2 font-semibold text-slate-100">{r.name || `Pt ${i + 1}`}</td>
                    <td className="px-2 py-2 text-slate-300">{fmt(r.altitude)}</td>
                    <td className="px-2 py-2 font-bold text-cyan-300">{fmt(r.headingMag)}°</td>
                    <td className="px-2 py-2 text-slate-300">{fmt(r.headingCompass)}°</td>
                    <td className="px-2 py-2 text-slate-300">{fmt(r.distance)}</td>
                    <td className="px-2 py-2 text-slate-300">{fmt(r.gs)} kt</td>
                    <td className="px-2 py-2 text-slate-300">{fmt(r.timeMin)}′</td>
                    <td className="px-2 py-2 text-slate-400">{minToHm(r.cumTime)}</td>
                    <td className="px-2 py-2 text-slate-300">{fmt(r.fuel, 1)} L</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        ))}

        {/* Fuel + WB */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="glass rounded-2xl p-5">
            <div className="mb-3 flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-cyan-400"><Fuel className="h-4 w-4" /> Bilan carburant</div>
            {plans.map(({ title, plan }) => (
              <div key={title} className="mb-2">
                {title && <div className="mb-1 text-[11px] uppercase tracking-wider text-cyan-500/80">{title}</div>}
                <Line l="Minimum réglementaire" v={`${fmt(plan.minRequired, 1)} L`} />
                <Line l="Réserve exploitant CDB" v={`${fmt(plan.cdb, 1)} L`} />
                <Line l="Embarqué" v={`${fmt(plan.onboard, 1)} L`} />
                <Line l="Marge" v={`${plan.margin >= 0 ? "+" : ""}${fmt(plan.margin, 1)} L`} accent={plan.margin >= 0 ? "text-emerald-300" : "text-rose-300"} />
              </div>
            ))}
          </div>
          <div className="glass rounded-2xl p-5">
            <div className="mb-3 flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wide text-cyan-400"><Scale className="h-4 w-4" /> Masse & centrage</div>
            <Line l="Décollage" v={`${fmt(wb.takeoff.weight)} kg · CG ${fmt(wb.takeoff.cg, 3)} m`} accent={wb.takeoff.ok ? "text-emerald-300" : "text-rose-300"} />
            <Line l="Atterrissage" v={`${fmt(wb.landing.weight)} kg · CG ${fmt(wb.landing.cg, 3)} m`} accent={wb.landing.ok ? "text-emerald-300" : "text-rose-300"} />
            <Line l="MTOW" v={`${fmt(wb.mtow)} kg`} />
          </div>
        </div>

        {/* Checklist */}
        <div className="glass rounded-2xl p-6">
          <div className="mb-4 font-display text-sm font-semibold uppercase tracking-wide text-cyan-400">Checklist 11 points</div>
          <ul className="space-y-3">
            {CHECKLIST_POINTS.map((p) => {
              const st = briefing.checklist?.[p.id] || {};
              const customSubs = st.customSubs || [];
              const subs = [
                ...(p.fields || []).map((f) => ({ key: f.key, label: f.label })),
                ...customSubs.map((c) => ({ key: c.key, label: c.label })),
              ];
              const pFiles = (data.files || []).filter((f) => String(f.point_id).startsWith(`${p.id}`));
              return (
                <li key={p.id} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                  <div className="flex items-center gap-2">
                    {st.done ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Circle className="h-4 w-4 text-slate-600" />}
                    <span className="font-medium text-slate-100">{p.id}. {p.title}</span>
                  </div>
                  {subs.map((sub) => st[sub.key] ? (
                    <div key={sub.key} className="mt-2 text-sm">
                      <span className="text-[11px] uppercase tracking-wider text-slate-500">{sub.label} : </span>
                      <span className="whitespace-pre-wrap text-slate-300">{st[sub.key]}</span>
                    </div>
                  ) : null)}
                  {pFiles.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {pFiles.map((f) => (
                        <a key={f.id} href={`${API}/shared/${token}/files/${f.id}/download`} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-950/40 px-3 py-1.5 text-xs text-slate-300 hover:border-cyan-500/40 hover:text-cyan-300">
                          {f.content_type?.startsWith("image/") ? <ImageIcon className="h-3.5 w-3.5" /> : <FileText className="h-3.5 w-3.5" />}
                          {f.original_filename} <ExternalLink className="h-3 w-3" />
                        </a>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        <CommentsPanel comments={data.comments || []} onPost={postComment} guest={!localStorage.getItem("vfr_token")} />
      </main>
    </div>
  );
}

const Tile = ({ icon: Icon, label, value, small }) => (
  <div className="glass rounded-xl p-4">
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-400"><Icon className="h-3.5 w-3.5 text-cyan-500" /> {label}</div>
    <div className={`mt-1 font-mono-data font-bold text-slate-100 ${small ? "text-sm" : "text-xl"}`}>{value}</div>
  </div>
);

const Line = ({ l, v, accent }) => (
  <div className="flex items-center justify-between border-b border-slate-800 py-2 text-sm last:border-0">
    <span className="text-slate-400">{l}</span>
    <span className={`font-mono-data font-semibold ${accent || "text-slate-100"}`}>{v}</span>
  </div>
);
